import React, { useEffect, useState, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import { auth } from '../../../firebase/config';
import Button from '../../../components/Button';
import Toast from '../../../components/Toast';
import Icon from '../../../components/Icon';
import Modal from '../../../components/Modal';
import PakistanPlaceSearch from '../../../components/PakistanPlaceSearch';
import BranchMapPickerModal from '../../../components/BranchMapPickerModal';
import Table, { Column } from '../../../components/Table';
import { useAppSelector } from '../../../store';
import {
    fetchProtocols,
    addProtocol,
    updateProtocol,
    deleteProtocol,
    fetchManagedPsoIds,
    type Protocol,
} from '../service/protocolService';
import {
    FiPlus,
    FiSearch,
    FiEye,
    FiEdit2,
    FiTrash2,
    FiRefreshCw,
    FiMapPin,
    FiClock,
    FiCalendar,
    FiBriefcase,
    FiMap,
    FiActivity,
    FiDownload,
    FiChevronDown,
    FiFilter,
    FiX
} from 'react-icons/fi';

interface ProtocolFormData {
    type: 'Receiving' | 'Dispatching';
    locationType: 'Hotel' | 'Airport';
    dateTime: string;
    address: string;
    latitude: string;
    longitude: string;
    reachTime: string;
    remarks: string;
}

const defaultFormData: ProtocolFormData = {
    type: 'Receiving',
    locationType: 'Airport',
    dateTime: '',
    address: '',
    latitude: '24.8607',
    longitude: '67.0011',
    reachTime: '',
    remarks: '',
};

export default function Protocols() {
    const [protocols, setProtocols] = useState<Protocol[]>([]);
    const [filtered, setFiltered] = useState<Protocol[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [selectedTypeFilter, setSelectedTypeFilter] = useState<'All' | 'Receiving' | 'Dispatching'>('All');
    const [draftTypeFilter, setDraftTypeFilter] = useState<'All' | 'Receiving' | 'Dispatching'>('All');
    
    // New Filters
    const [selectedPsoFilter, setSelectedPsoFilter] = useState('All');
    const [draftPsoFilter, setDraftPsoFilter] = useState('All');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [draftStartDate, setDraftStartDate] = useState('');
    const [draftEndDate, setDraftEndDate] = useState('');

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    
    // Export Dropdown State
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const exportMenuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
                setExportMenuOpen(false);
            }
        };
        if (exportMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [exportMenuOpen]);

    const handleExportExcel = () => {
        if (filtered.length === 0) {
            showToast('No data to export', 'warning');
            return;
        }

        const dateStr = new Date().toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

        const psoName = selectedPsoFilter !== 'All' ? (uniquePsos.find(p => p.id === selectedPsoFilter)?.name || selectedPsoFilter) : 'All';
        
        const summaryRows = [
            ['PSO WEB - PROTOCOL SUMMARY REPORT'],
            [`Generated: ${dateStr}`],
            [''],
            ['REPORT SUMMARY'],
            [`Total Records: ${filtered.length}`],
            [`Type Filter: ${selectedTypeFilter}`],
            [`Protocol Officer: ${psoName}`],
            [`Date Range: ${startDate || 'Any'} to ${endDate || 'Any'}`],
            [''],
            ['Protocol Type', 'Origin', 'Pick/Drop Date & Time', 'Officer Arrival', 'Destination Address', 'Coordinates', 'Created By', 'Remarks']
        ];

        const dataRows = filtered.map((p) => [
            p.type || 'N/A',
            p.locationType || 'N/A',
            p.dateTime ? new Date(p.dateTime).toLocaleString() : 'N/A',
            p.reachTime ? new Date(p.reachTime).toLocaleString() : 'N/A',
            p.address || 'N/A',
            `Lat: ${p.latitude || 'N/A'}, Long: ${p.longitude || 'N/A'}`,
            p.createdBy || 'N/A',
            p.remarks || 'N/A',
        ]);

        const ws = XLSX.utils.aoa_to_sheet([...summaryRows, ...dataRows]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Protocols');
        
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `Protocols_Summary_${new Date().getTime()}.xlsx`);
        setExportMenuOpen(false);
        showToast('Excel exported successfully!', 'success');
    };

    const handleExportPDF = () => {
        if (filtered.length === 0) {
            showToast('No data to export', 'warning');
            return;
        }

        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

        // Colors
        const primaryColor: [number, number, number] = [15, 23, 42];
        const dateStr = new Date().toLocaleString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
        });
        const psoName = selectedPsoFilter !== 'All' ? (uniquePsos.find(p => p.id === selectedPsoFilter)?.name || selectedPsoFilter) : 'All';

        doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.rect(0, 0, 297, 35, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(20);
        doc.text('PSO WEB - PROTOCOL SUMMARY REPORT', 15, 18);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(200, 220, 255);
        doc.text(`Generated: ${dateStr}`, 15, 26);

        // Filters Summary Box
        doc.setFillColor(30, 41, 59);
        doc.rect(190, 8, 92, 20, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text('REPORT SUMMARY', 195, 13);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(200, 200, 200);
        doc.text(`Total Records: ${filtered.length} | Type: ${selectedTypeFilter}`, 195, 18);
        doc.text(`Protocol Officer: ${psoName}`, 195, 22);
        doc.text(`Date Range: ${startDate || 'Any'} to ${endDate || 'Any'}`, 195, 26);

        const tableColumn = ["Type", "Origin", "Pick/Drop Date", "Arrival Time", "Address", "Created By", "Remarks"];
        const tableRows = filtered.map(p => [
            p.type || 'N/A',
            p.locationType || 'N/A',
            p.dateTime ? new Date(p.dateTime).toLocaleString() : 'N/A',
            p.reachTime ? new Date(p.reachTime).toLocaleString() : 'N/A',
            p.address || 'N/A',
            p.createdBy || 'N/A',
            (p.remarks || '').substring(0, 60) + ((p.remarks || '').length > 60 ? '...' : '')
        ]);

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 45,
            theme: 'grid',
            headStyles: { fillColor: [59, 130, 246] },
            styles: { fontSize: 8 },
            margin: { left: 15, right: 15 }
        });

        doc.save(`Protocols_Summary_${new Date().getTime()}.pdf`);
        setExportMenuOpen(false);
        showToast('PDF exported successfully!', 'success');
    };

    // Modals
    const [showFormModal, setShowFormModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [mapOpen, setMapOpen] = useState(false);
    
    const [selectedProtocol, setSelectedProtocol] = useState<Protocol | null>(null);
    const [formData, setFormData] = useState<ProtocolFormData>(defaultFormData);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({
        show: false,
        message: '',
        type: 'success',
    });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 15;

    // Roles and Bank Id
    const pso = useAppSelector((s) => s.user.pso);
    const bank = useAppSelector((s) => s.user.bank);
    // Use the real Firestore document ID (bankDocId) if available, else fall back to sessionStorage,
    // then companyId. The companyId field may differ from the actual Firestore doc ID.
    const bankId = bank?.bankDocId || sessionStorage.getItem('bankDocId') || bank?.companyId || '';
    const psoDocId = sessionStorage.getItem('psoDocId') || pso?.authUid || '';
    
    const isProtocolOfficer = pso?.designation?.toLowerCase().replace(/_/g, ' ') === 'protocol officer';
    const activeUserEmail = pso?.email || auth.currentUser?.email || 'N/A';

    useEffect(() => {
        if (!bankId) return;
        loadData();
    }, [bankId]);


    const loadData = async () => {
        setLoading(true);
        try {
            const data = await fetchProtocols(bankId);
            
            // Filter by regional assignment
            const designation = pso?.designation?.toLowerCase().replace(/_/g, ' ') || '';
            const isRegional = designation.includes('regional');
            let protocolsToDisplay = data;
            
            if (isRegional && psoDocId) {
                const managedIds = await fetchManagedPsoIds(bankId, psoDocId);
                protocolsToDisplay = data.filter(
                    (p) => 
                        p.createdByPsoId === pso?.authUid || 
                        (p.createdByPsoId && managedIds.includes(p.createdByPsoId))
                );
            } else if (isProtocolOfficer) {
                protocolsToDisplay = data.filter(
                    (p) => p.createdByPsoId === pso?.authUid || p.createdBy === activeUserEmail
                );
            }
            
            setProtocols(protocolsToDisplay);
            setFiltered(protocolsToDisplay);
        } catch (err: any) {
            console.error('Error fetching protocols:', err);
            showToast('Failed to load protocol data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setToast({ show: true, message, type });
    };

    const uniquePsos = useMemo(() => {
        const map = new Map<string, { id: string, name: string }>();
        protocols.forEach(p => {
            if (p.createdBy) {
                const id = p.createdByPsoId || p.createdBy;
                map.set(id, { id, name: p.createdBy });
            }
        });
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [protocols]);

    useEffect(() => {
        let list = [...protocols];

        // Search Filter
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            list = list.filter(
                (p) =>
                    p.address?.toLowerCase().includes(term) ||
                    p.remarks?.toLowerCase().includes(term) ||
                    p.createdBy?.toLowerCase().includes(term)
            );
        }

        // Type Filter
        if (selectedTypeFilter !== 'All') {
            list = list.filter((p) => p.type === selectedTypeFilter);
        }

        // PSO Filter
        if (selectedPsoFilter !== 'All') {
            list = list.filter((p) => (p.createdByPsoId || p.createdBy) === selectedPsoFilter);
        }

        // Date Range Filter (against dateTime and reachTime conceptually, we'll just check dateTime)
        if (startDate || endDate) {
            list = list.filter(p => {
                if (!p.dateTime) return false;
                const pDate = new Date(p.dateTime);
                if (isNaN(pDate.getTime())) return true;
                pDate.setHours(0, 0, 0, 0);

                if (startDate && endDate) {
                    const start = new Date(startDate); start.setHours(0, 0, 0, 0);
                    const end = new Date(endDate); end.setHours(23, 59, 59, 999);
                    return pDate >= start && pDate <= end;
                } else if (startDate) {
                    const start = new Date(startDate); start.setHours(0, 0, 0, 0);
                    return pDate >= start;
                } else if (endDate) {
                    const end = new Date(endDate); end.setHours(23, 59, 59, 999);
                    return pDate <= end;
                }
                return true;
            });
        }

        setFiltered(list);
        setCurrentPage(1);
    }, [searchTerm, selectedTypeFilter, selectedPsoFilter, startDate, endDate, protocols]);

    const applySearch = () => {
        setSearchTerm(searchInput);
        setCurrentPage(1);
    };

    const applyFilters = () => {
        setSelectedTypeFilter(draftTypeFilter);
        setSelectedPsoFilter(draftPsoFilter);
        setStartDate(draftStartDate);
        setEndDate(draftEndDate);
        setShowFilterModal(false);
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setDraftTypeFilter('All');
        setSelectedTypeFilter('All');
        setDraftPsoFilter('All');
        setSelectedPsoFilter('All');
        setDraftStartDate('');
        setDraftEndDate('');
        setStartDate('');
        setEndDate('');
        setShowFilterModal(false);
        setCurrentPage(1);
    };

    const handleInputChange = (field: keyof ProtocolFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }));
        }
    };

    const handlePlaceResolved = (p: any) => {
        setFormData((prev) => ({
            ...prev,
            latitude: String(p.lat),
            longitude: String(p.lng),
            address: p.formattedAddress || prev.address,
        }));
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!formData.dateTime) {
            newErrors.dateTime = `${formData.type === 'Receiving' ? 'Pick' : 'Drop'} date and time is required`;
        }
        if (!formData.address.trim()) {
            newErrors.address = 'Destination address is required';
        }
        if (!formData.latitude || !formData.longitude) {
            newErrors.latitude = 'Map coordination pin point is required';
        }
        if (!formData.reachTime) {
            newErrors.reachTime = 'Destination officer arrival time is required';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleOpenCreateModal = () => {
        setSelectedProtocol(null);
        setFormData(defaultFormData);
        setErrors({});
        setShowFormModal(true);
    };

    const handleOpenEditModal = (p: Protocol) => {
        setSelectedProtocol(p);
        setFormData({
            type: p.type,
            locationType: p.locationType,
            dateTime: p.dateTime,
            address: p.address,
            latitude: p.latitude || '24.8607',
            longitude: p.longitude || '67.0011',
            reachTime: p.reachTime,
            remarks: p.remarks,
        });
        setErrors({});
        setShowFormModal(true);
    };

    const handleOpenDetailsModal = (p: Protocol) => {
        setSelectedProtocol(p);
        setShowDetailsModal(true);
    };

    const handleOpenDeleteModal = (p: Protocol) => {
        setSelectedProtocol(p);
        setShowDeleteModal(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isProtocolOfficer) {
            showToast('Only Protocol Officers can create or edit protocols', 'error');
            return;
        }
        if (!validateForm()) return;

        setSubmitting(true);
        try {
            if (selectedProtocol) {
                // Update
                await updateProtocol(bankId, selectedProtocol.id, formData);
                showToast('✅ Protocol updated successfully!', 'success');
            } else {
                // Add
                await addProtocol(bankId, {
                    ...formData,
                    createdBy: activeUserEmail,
                    createdByPsoId: pso?.authUid || '',
                });
                showToast('✅ Protocol created successfully!', 'success');
            }
            setShowFormModal(false);
            loadData();
        } catch (err: any) {
            console.error('Error saving protocol:', err);
            showToast(err.message || 'Failed to save protocol', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedProtocol) return;
        setSubmitting(true);
        try {
            await deleteProtocol(bankId, selectedProtocol.id);
            showToast('✅ Protocol deleted successfully!', 'success');
            setShowDeleteModal(false);
            loadData();
        } catch (err: any) {
            console.error('Error deleting protocol:', err);
            showToast('Failed to delete protocol', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // Statistics Card Computations
    const totalCount = protocols.length;
    const receivingCount = protocols.filter((p) => p.type === 'Receiving').length;
    const dispatchingCount = protocols.filter((p) => p.type === 'Dispatching').length;

    // Formatting date helper
    const formatDateTime = (isoStr: string) => {
        if (!isoStr) return 'N/A';
        try {
            const date = new Date(isoStr);
            return date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return isoStr;
        }
    };

    // Pakistan Maps Key
    const gmapsKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    // Table Columns
    const columns: Column<Protocol>[] = [
        {
            header: 'Type',
            width: '10%',
            render: (p) => (
                <span
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                        p.type === 'Receiving'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300'
                    }`}
                >
                    <Icon icon={FiActivity} className="h-3 w-3" />
                    {p.type}
                </span>
            ),
        },
        {
            header: 'Origin',
            width: '10%',
            render: (p) => (
                <span
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                        p.locationType === 'Hotel'
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                            : 'bg-purple-100 text-purple-800 dark:bg-purple-950/40 dark:text-purple-300'
                    }`}
                >
                    <Icon icon={FiMapPin} className="h-3 w-3" />
                    {p.locationType}
                </span>
            ),
        },
        ...(!isProtocolOfficer ? [{
            header: 'Protocol Officer',
            width: '14%',
            render: (p: Protocol) => (
                <div className="flex flex-col">
                    <span className="text-xs font-medium text-gray-900 dark:text-gray-100">{p.createdBy || 'N/A'}</span>
                </div>
            )
        }] : []),
        {
            header: 'Pick/Drop Time',
            width: '18%',
            render: (p) => (
                <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
                    <Icon icon={FiCalendar} className="h-4 w-4 text-gray-400" />
                    <span className="font-medium text-xs">{formatDateTime(p.dateTime)}</span>
                </div>
            ),
        },
        {
            header: 'Officer Arrival',
            width: '18%',
            render: (p) => (
                <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
                    <Icon icon={FiClock} className="h-4 w-4 text-gray-400" />
                    <span className="font-medium text-xs">{formatDateTime(p.reachTime)}</span>
                </div>
            ),
        },
        {
            header: 'Destination Address',
            width: '20%',
            render: (p) => (
                <div className="max-w-xs truncate text-xs text-gray-600 dark:text-gray-400" title={p.address}>
                    {p.address || 'N/A'}
                </div>
            ),
        },
        {
            header: 'Actions',
            width: '10%',
            align: 'center',
            render: (p) => (
                <div className="flex items-center justify-center gap-2">
                    <button
                        onClick={() => handleOpenDetailsModal(p)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:bg-gray-50 hover:text-blue-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                        title="View Details"
                    >
                        <Icon icon={FiEye} className="h-4 w-4" />
                    </button>
                    {isProtocolOfficer && (
                        <>
                            <button
                                onClick={() => handleOpenEditModal(p)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:bg-gray-50 hover:text-amber-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                                title="Edit Protocol"
                            >
                                <Icon icon={FiEdit2} className="h-3.5 w-3.5" />
                            </button>
                            <button
                                onClick={() => handleOpenDeleteModal(p)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:bg-gray-50 hover:text-red-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                                title="Delete Protocol"
                            >
                                <Icon icon={FiTrash2} className="h-3.5 w-3.5" />
                            </button>
                        </>
                    )}
                </div>
            ),
        },
    ];



    const paginatedData = useMemo(() => {
        return filtered.slice((currentPage - 1) * perPage, currentPage * perPage);
    }, [filtered, currentPage]);

    const totalPages = Math.ceil(filtered.length / perPage);

    return (
        <>
            <div className="space-y-6">
                {/* Statistics Overview */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    {/* Card 1: Total Protocols */}
                    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-black/30">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                    Total Protocols
                                </p>
                                <p className="mt-2 text-3xl font-extrabold text-gray-900 dark:text-gray-100">
                                    {totalCount}
                                </p>
                            </div>
                            <div className="rounded-full bg-blue-50 p-3 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
                                <Icon icon={FiBriefcase} className="h-6 w-6" />
                            </div>
                        </div>
                        <div className="mt-4 h-1.5 w-full rounded-full bg-gray-100 dark:bg-gray-800">
                            <div className="h-full bg-blue-500 rounded-full" style={{ width: '100%' }}></div>
                        </div>
                    </div>

                    {/* Card 2: Receiving Protocols */}
                    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-black/30">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                    Receiving Protocols
                                </p>
                                <p className="mt-2 text-3xl font-extrabold text-gray-900 dark:text-gray-100">
                                    {receivingCount}
                                </p>
                            </div>
                            <div className="rounded-full bg-emerald-50 p-3 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400">
                                <Icon icon={FiActivity} className="h-6 w-6" />
                            </div>
                        </div>
                        <div className="mt-4 h-1.5 w-full rounded-full bg-gray-100 dark:bg-gray-800">
                            <div
                                className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                                style={{ width: `${totalCount > 0 ? (receivingCount / totalCount) * 100 : 0}%` }}
                            ></div>
                        </div>
                    </div>

                    {/* Card 3: Dispatching Protocols */}
                    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-black/30">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                    Dispatching Protocols
                                </p>
                                <p className="mt-2 text-3xl font-extrabold text-gray-900 dark:text-gray-100">
                                    {dispatchingCount}
                                </p>
                            </div>
                            <div className="rounded-full bg-purple-50 p-3 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400">
                                <Icon icon={FiMapPin} className="h-6 w-6" />
                            </div>
                        </div>
                        <div className="mt-4 h-1.5 w-full rounded-full bg-gray-100 dark:bg-gray-800">
                            <div
                                className="h-full bg-purple-500 rounded-full transition-all duration-500"
                                style={{ width: `${totalCount > 0 ? (dispatchingCount / totalCount) * 100 : 0}%` }}
                            ></div>
                        </div>
                    </div>
                </div>

                {/* Search and Filter Controls */}
                <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex w-full flex-1 items-stretch gap-3 md:pr-3">
                        <div className="relative flex-1 min-w-0">
                            <Icon icon={FiSearch} className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by address, remarks, or creator..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        applySearch();
                                    }
                                }}
                                className="w-full h-11 rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                            />
                            {searchInput && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSearchInput('');
                                        setSearchTerm('');
                                        setCurrentPage(1);
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                                    title="Clear search"
                                >
                                    <Icon icon={FiX} className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                        <Button onClick={applySearch} className="!h-11 !py-0 flex items-center gap-2 shrink-0">
                            <Icon icon={FiSearch} className="h-4 w-4" />
                            Search
                        </Button>
                    </div>
                    
                    <div className="flex shrink-0 flex-wrap items-center gap-3">
                        {/* Export Menu */}
                        <div className="relative" ref={exportMenuRef}>
                            <button
                                type="button"
                                onClick={() => setExportMenuOpen((o) => !o)}
                                aria-expanded={exportMenuOpen}
                                className="inline-flex h-11 items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                            >
                                <Icon icon={FiDownload} className="h-4 w-4" />
                                Export
                                <Icon icon={FiChevronDown} className={`h-4 w-4 shrink-0 transition-transform ${exportMenuOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {exportMenuOpen && (
                                <div className="absolute right-0 z-40 mt-1 w-52 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl ring-1 ring-black/5 dark:border-gray-600 dark:bg-gray-900">
                                    <button
                                        type="button"
                                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-emerald-50 dark:text-gray-100 dark:hover:bg-emerald-900/20"
                                        onClick={handleExportExcel}
                                    >
                                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600 text-xs font-bold text-white">
                                            XLS
                                        </span>
                                        <span>
                                            <span className="block font-medium">Excel workbook</span>
                                            <span className="text-[10px] text-gray-500 dark:text-gray-400">Current filtered view</span>
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-rose-50 dark:text-gray-100 dark:hover:bg-rose-900/20"
                                        onClick={handleExportPDF}
                                    >
                                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-600 text-xs font-bold text-white">
                                            PDF
                                        </span>
                                        <span>
                                            <span className="block font-medium">PDF report</span>
                                            <span className="text-[10px] text-gray-500 dark:text-gray-400">Professional Summary</span>
                                        </span>
                                    </button>
                                </div>
                            )}
                        </div>

                        <Button variant="secondary" onClick={() => setShowFilterModal(true)} className="flex h-11 items-center gap-2">
                            <Icon icon={FiFilter} className="h-4 w-4" />
                            Filters
                        </Button>

                        {selectedTypeFilter !== 'All' && (
                            <Button variant="secondary" onClick={clearFilters} className="h-11">
                                Clear Filters
                            </Button>
                        )}

                        {isProtocolOfficer && (
                            <Button onClick={handleOpenCreateModal} className="flex h-11 items-center gap-2 font-semibold">
                                <Icon icon={FiPlus} className="h-4 w-4" />
                                Create Protocol
                            </Button>
                        )}
                    </div>
                </div>

                {/* Primary Data Roster Table */}
                <Table
                    title="Protocols Directory"
                    description="Listing receiving and dispatching schedules mapped to designated targets."
                    data={paginatedData}
                    columns={columns}
                    keyExtractor={(p) => p.id}
                    loading={loading}
                    emptyMessage={searchTerm || selectedTypeFilter !== 'All' ? 'No protocols found matching the search' : 'No protocol entries available.'}
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: setCurrentPage,
                    }}
                />
            </div>

            {/* Create & Edit Modal Form */}
            <Modal
                isOpen={showFormModal}
                onClose={() => setShowFormModal(false)}
                title={selectedProtocol ? 'Edit Protocol Sheet' : 'Create New Protocol Sheet'}
                size="lg"
                closeOnBackdropClick={false}
            >
                <form onSubmit={handleSubmit} className="p-6">
                    <div className="space-y-5">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                                    Protocol Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={formData.type}
                                    onChange={(e) => handleInputChange('type', e.target.value as any)}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 px-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                >
                                    <option value="Receiving">Receiving</option>
                                    <option value="Dispatching">Dispatching</option>
                                </select>
                            </div>

                            <div>
                                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                                    Origin Location <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={formData.locationType}
                                    onChange={(e) => handleInputChange('locationType', e.target.value as any)}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 px-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                >
                                    <option value="Airport">Airport</option>
                                    <option value="Hotel">Hotel</option>
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                                {formData.type === 'Receiving' ? 'Pick Date & Time' : 'Drop Date & Time'}{' '}
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="datetime-local"
                                value={formData.dateTime}
                                onChange={(e) => handleInputChange('dateTime', e.target.value)}
                                className={`w-full rounded-xl border py-2.5 px-3 text-sm outline-none transition bg-white text-gray-900 border-gray-300 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600 ${
                                    errors.dateTime ? 'border-red-500 focus:ring-red-500/10' : 'focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10'
                                }`}
                                required
                            />
                            {errors.dateTime && <p className="mt-1 text-xs text-red-500">{errors.dateTime}</p>}
                        </div>

                        {/* Maps pin pointing search */}
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:items-end">
                            <div className="lg:col-span-2">
                                <PakistanPlaceSearch apiKey={gmapsKey} onResolved={handlePlaceResolved} />
                            </div>
                            <div>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => setMapOpen(true)}
                                    className="w-full justify-center text-sm py-2.5 flex items-center gap-2 border border-gray-300 dark:border-gray-600"
                                >
                                    <Icon icon={FiMap} className="h-4 w-4" />
                                    Pick on Map
                                </Button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                                    Longitude
                                </label>
                                <input
                                    type="text"
                                    value={formData.longitude}
                                    onChange={(e) => handleInputChange('longitude', e.target.value)}
                                    placeholder="67.0011"
                                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 px-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                                    Latitude
                                </label>
                                <input
                                    type="text"
                                    value={formData.latitude}
                                    onChange={(e) => handleInputChange('latitude', e.target.value)}
                                    placeholder="24.8607"
                                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 px-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                                Destination Address <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.address}
                                onChange={(e) => handleInputChange('address', e.target.value)}
                                placeholder="Enter specific destination address"
                                className={`w-full rounded-xl border py-2.5 px-3 text-sm outline-none transition bg-white text-gray-900 border-gray-300 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600 ${
                                    errors.address ? 'border-red-500 focus:ring-red-500/10' : 'focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10'
                                }`}
                                required
                            />
                            {errors.address && <p className="mt-1 text-xs text-red-500">{errors.address}</p>}
                        </div>

                        <div>
                            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                                Time Officer Reach Destination <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="datetime-local"
                                value={formData.reachTime}
                                onChange={(e) => handleInputChange('reachTime', e.target.value)}
                                className={`w-full rounded-xl border py-2.5 px-3 text-sm outline-none transition bg-white text-gray-900 border-gray-300 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600 ${
                                    errors.reachTime ? 'border-red-500 focus:ring-red-500/10' : 'focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10'
                                }`}
                                required
                            />
                            {errors.reachTime && <p className="mt-1 text-xs text-red-500">{errors.reachTime}</p>}
                        </div>

                        <div>
                            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                                Remarks / Special Notes
                            </label>
                            <textarea
                                value={formData.remarks}
                                onChange={(e) => handleInputChange('remarks', e.target.value)}
                                placeholder="Add any special instructions or remarks here..."
                                rows={3}
                                className="w-full rounded-xl border border-gray-300 bg-white py-2 px-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100 resize-none"
                            />
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => setShowFormModal(false)}
                                disabled={submitting}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={submitting}>
                                {submitting ? 'Saving...' : 'Save Protocol'}
                            </Button>
                        </div>
                    </div>
                </form>
            </Modal>

            {/* Read-Only Details Modal */}
            <Modal
                isOpen={showDetailsModal}
                onClose={() => {
                    setShowDetailsModal(false);
                    setSelectedProtocol(null);
                }}
                title="Protocol Details View"
                size="md"
            >
                {selectedProtocol && (
                    <div className="p-6 space-y-6">
                        <div className="flex flex-wrap gap-3 items-center justify-between border-b border-gray-100 pb-4 dark:border-gray-800">
                            <div className="flex gap-2">
                                <span
                                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${
                                        selectedProtocol.type === 'Receiving'
                                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
                                            : 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300'
                                    }`}
                                >
                                    {selectedProtocol.type}
                                </span>
                                <span
                                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${
                                        selectedProtocol.locationType === 'Hotel'
                                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                                            : 'bg-purple-100 text-purple-800 dark:bg-purple-950/40 dark:text-purple-300'
                                    }`}
                                >
                                    {selectedProtocol.locationType}
                                </span>
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                Created by: <span className="font-semibold text-gray-800 dark:text-gray-200">{selectedProtocol.createdBy}</span>
                            </span>
                        </div>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="rounded-xl bg-gray-50 p-4 dark:bg-gray-800/40">
                                <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                    {selectedProtocol.type === 'Receiving' ? 'Pick Date & Time' : 'Drop Date & Time'}
                                </span>
                                <span className="mt-1.5 block text-sm font-bold text-gray-800 dark:text-gray-100">
                                    {formatDateTime(selectedProtocol.dateTime)}
                                </span>
                            </div>

                            <div className="rounded-xl bg-gray-50 p-4 dark:bg-gray-800/40">
                                <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                    Officer Destination Arrival
                                </span>
                                <span className="mt-1.5 block text-sm font-bold text-gray-800 dark:text-gray-100">
                                    {formatDateTime(selectedProtocol.reachTime)}
                                </span>
                            </div>
                        </div>

                        <div className="rounded-xl bg-gray-50 p-4 dark:bg-gray-800/40 space-y-2">
                            <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                Destination Coordinates
                            </span>
                            {selectedProtocol.latitude && selectedProtocol.longitude && gmapsKey ? (
                                <div className="mt-2 w-full h-48 rounded-xl overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700">
                                    <iframe
                                        width="100%"
                                        height="100%"
                                        style={{ border: 0 }}
                                        loading="lazy"
                                        allowFullScreen
                                        src={`https://www.google.com/maps/embed/v1/place?key=${gmapsKey}&q=${selectedProtocol.latitude},${selectedProtocol.longitude}`}
                                    ></iframe>
                                </div>
                            ) : (
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Coordinates not provided</span>
                            )}
                        </div>

                        <div className="rounded-xl bg-gray-50 p-4 dark:bg-gray-800/40">
                            <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                Destination Address
                            </span>
                            <span className="mt-1.5 block text-sm font-medium text-gray-700 dark:text-gray-200 break-words leading-relaxed">
                                {selectedProtocol.address || 'N/A'}
                            </span>
                        </div>

                        <div className="rounded-xl bg-gray-50 p-4 dark:bg-gray-800/40">
                            <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                Remarks / Notes
                            </span>
                            <span className="mt-1.5 block text-sm font-medium text-gray-700 dark:text-gray-200 break-words leading-relaxed whitespace-pre-wrap">
                                {selectedProtocol.remarks || 'No remarks provided.'}
                            </span>
                        </div>

                        <div className="flex justify-end pt-2 border-t border-gray-100 dark:border-gray-800">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => {
                                    setShowDetailsModal(false);
                                    setSelectedProtocol(null);
                                }}
                            >
                                Close Details
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setSelectedProtocol(null);
                }}
                title="Confirm Delete Protocol"
                size="sm"
            >
                <div className="p-6 space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Are you sure you want to permanently delete this protocol schedule? This action cannot be undone.
                    </p>
                    <div className="flex justify-end gap-3 pt-2">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => {
                                setShowDeleteModal(false);
                                setSelectedProtocol(null);
                            }}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={handleDelete}
                            disabled={submitting}
                            className="bg-red-600 hover:bg-red-700 focus:ring-red-500/20 text-white"
                        >
                            {submitting ? 'Deleting...' : 'Delete'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Filter Modal */}
            <Modal
                isOpen={showFilterModal}
                onClose={() => setShowFilterModal(false)}
                title="Filter Protocols"
                footer={
                    <div className="flex justify-between gap-2 px-6 py-4">
                        <Button variant="secondary" onClick={clearFilters} className="text-sm">
                            Clear All
                        </Button>
                        <div className="flex gap-2">
                            <Button variant="secondary" onClick={() => setShowFilterModal(false)}>
                                Cancel
                            </Button>
                            <Button onClick={applyFilters}>
                                Apply Filters
                            </Button>
                        </div>
                    </div>
                }
            >
                <div className="space-y-5 px-6 py-5">
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Protocol Type
                        </label>
                        <select
                            value={draftTypeFilter}
                            onChange={(e) => setDraftTypeFilter(e.target.value as any)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-2.5 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                        >
                            <option value="All">All Types</option>
                            <option value="Receiving">Receiving</option>
                            <option value="Dispatching">Dispatching</option>
                        </select>
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Protocol Officer
                        </label>
                        <select
                            value={draftPsoFilter}
                            onChange={(e) => setDraftPsoFilter(e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-2.5 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                        >
                            <option value="All">All Officers</option>
                            {uniquePsos.map(pso => (
                                <option key={pso.id} value={pso.id}>
                                    {pso.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Pick/Drop Date Range
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <input
                                type="date"
                                value={draftStartDate}
                                onChange={(e) => setDraftStartDate(e.target.value)}
                                className="w-full rounded-xl border border-gray-300 bg-white py-2.5 px-3 text-sm outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                            />
                            <input
                                type="date"
                                value={draftEndDate}
                                onChange={(e) => setDraftEndDate(e.target.value)}
                                className="w-full rounded-xl border border-gray-300 bg-white py-2.5 px-3 text-sm outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                            />
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Google Maps Coordinates Selector Modal */}
            <BranchMapPickerModal
                isOpen={mapOpen}
                onClose={() => setMapOpen(false)}
                apiKey={gmapsKey}
                latitude={formData.latitude}
                longitude={formData.longitude}
                onSubmit={(lat, lng) => {
                    setFormData((prev) => ({
                        ...prev,
                        latitude: String(lat),
                        longitude: String(lng),
                    }));
                }}
            />

            {/* Toast Notifications */}
            {toast.show && (
                <div className="pointer-events-none fixed right-4 top-4 z-50">
                    <div className="pointer-events-auto">
                        <Toast
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToast({ ...toast, show: false })}
                        />
                    </div>
                </div>
            )}
        </>
    );
}
